import {
  Product,
  SaleLineItem,
} from "@towersystems/roam-common/lib/generated-types";
import { Spacings, View } from "react-native-ui-lib";
import {
  ProductThumbnail,
  Stack,
  TextButton,
  Tooltip,
} from "@towersystems/roam-common-native";
import { moneyToFormattedString } from "../../../../../../utilities/money";
import { toTitleCase } from "../../../../../../utilities/to-title-case";
import { Text } from "@towersystems/roam-common-native";
import {
  bankersRound,
  formatDateToCalendar,
} from "@towersystems/roam-common/lib/shared-utils";
import { obscureText } from "../../../../../../utilities/utils";
import { toInternalProductType } from "../../../../../../utilities/to-internal-product-type";
import { Accordion } from "../../../../../../components/Accordion";
import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { QUERY_PRODUCT } from "../../../../../products/utils";
import { UseAwaitReturn } from "../../../../../../utilities/use-await";
import { Pressable } from "react-native";
import { AntDesign } from "@expo/vector-icons";

export interface LineItemProps {
  lineItem: SaleLineItem;
  onItemPress?: (item: SaleLineItem) => void;
}

type ProductMapType = { [key: string]: Product };

export const LineItem = ({ lineItem, onItemPress }: LineItemProps) => {
  const [expanded, setExpanded] = useState(true);
  const [productMapState, setProductMapState] =
    useState<UseAwaitReturn<ProductMapType> | null>(null);
  const [queryProduct] = useLazyQuery(QUERY_PRODUCT);

  async function fetchKitProducts() {
    const productIds = Object.values(lineItem.metadata?.components || {}).map(
      (component: any) => {
        return component.productId;
      },
    );

    return Promise.all(
      productIds.map((productId) =>
        queryProduct({ variables: { id: productId } }),
      ),
    );
  }

  useEffect(() => {
    fetchKitProducts()
      .then((results) => {
        const newMapData = results.reduce((map, result) => {
          if (result.data?.product) {
            map[result.data.product.id] = result.data.product;
          }
          return map;
        }, {} as ProductMapType);

        setProductMapState({
          isLoading: false,
          data: newMapData,
          isError: false,
        });
      })
      .catch((error) => {
        console.error(error);
        setProductMapState({
          isLoading: false,
          data: {},
          isError: true,
          errorMsg: "Something went wrong loading kit items",
        });
      });
  }, []);

  if (!lineItem.isKit) {
    return <LineItemRenderer lineItem={lineItem} onItemPress={onItemPress} />;
  }

  function handlePress() {
    setExpanded((prev) => !prev);
  }

  let kitItemsMarkup = null;

  if (!productMapState || productMapState?.isLoading) {
    kitItemsMarkup = <Text>Loading...</Text>;
  } else if (productMapState?.isError) {
    kitItemsMarkup = <Text>{productMapState.errorMsg}</Text>;
  } else {
    // mock line item with kit component and product
    kitItemsMarkup = Object.values(lineItem.metadata?.components || {}).map(
      (component: any) => {
        const product = productMapState.data?.[component.productId];
        if (!product) {
          return null;
        }
        const kitLineItem = kitItemToSaleItem(component, product);
        return (
          <LineItemRenderer
            key={component.id}
            lineItem={kitLineItem as any}
            isKitComponent
          />
        );
      },
    );
  }

  return (
    <Accordion
      viewKey={`product-kit-accordion_${expanded}`}
      header={
        <View
          style={{
            flex: 1,
            height: "100%",
            paddingTop: 8,
            paddingBottom: 8,
          }}
        >
          <LineItemRenderer lineItem={lineItem} onItemPress={onItemPress} />
        </View>
      }
      expanded={expanded}
      onPress={handlePress}
    >
      {Object.values(lineItem.metadata?.components || {}).length ? (
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            paddingHorizontal: Spacings.s6,
            paddingTop: Spacings.s1,
          }}
        >
          <View style={{ opacity: 0 }}>
            <Pressable onPress={() => {}} style={{ marginRight: Spacings.s3 }}>
              <AntDesign name={"up"} size={24} color={"white"} />
            </Pressable>
          </View>
          <Stack vertical spacing={Spacings.s3}>
            {kitItemsMarkup}
          </Stack>
        </View>
      ) : null}
    </Accordion>
  );
};

type LineItemRendererProps = {
  lineItem: SaleLineItem;
  isKitComponent?: boolean;
  onItemPress?: (item: SaleLineItem) => void;
};

function LineItemRenderer({
  lineItem,
  isKitComponent,
  onItemPress,
}: LineItemRendererProps) {
  return (
    <View style={{ flex: 1, height: "100%" }}>
      <View
        flex
        row
        style={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <View
          style={{ alignItems: "center", flexBasis: "100%", flexShrink: 1 }}
          row
        >
          <ProductThumbnail
            imageUrl={lineItem.product?.image?.path}
            quantity={isKitComponent ? undefined : lineItem.quantity}
            isInternalProduct={lineItem.product?.internal}
            internalProductType={
              lineItem.product && toInternalProductType(lineItem.product)
            }
          />
          <View marginL-20 style={{ flexShrink: 1 }}>
            {onItemPress ? (
              <Tooltip text="View AI Insights">
                <TextButton
                  textStyle={{
                    fontSize: 18,
                  }}
                  onPress={() => onItemPress(lineItem)}
                >
                  {toTitleCase(lineItem.name ?? (lineItem.product?.name || ""))}
                </TextButton>
              </Tooltip>
            ) : (
              <Text variant="heading1" numberOfLines={1} ellipsizeMode="head">
                {toTitleCase(lineItem.name ?? (lineItem.product?.name || ""))}
              </Text>
            )}
            <Text>{lineItem.product?.displayName}</Text>
            <Text color="subdued">
              Stock Number:{" "}
              {lineItem.stockNumber || lineItem.product?.stockNumber || ""}
            </Text>
            {lineItem.serialNumber ? (
              <Text color="subdued">S/N: {lineItem.serialNumber}</Text>
            ) : null}
            {lineItem.metadata?.surcharge === true ? (
              <Text color="subdued">
                {lineItem.metadata?.surchargeDescription}
              </Text>
            ) : null}
            {lineItem.customerNote ? (
              <View>
                <Text numberOfLines={4} color="subdued">
                  {`"${lineItem.customerNote}"`}
                </Text>
              </View>
            ) : null}
            {lineItem.giftCardNumber ? (
              <Text color="subdued">
                {`Gift Card Number: ${
                  lineItem.metadata.giftVoucherActivatedDate
                    ? obscureText(lineItem.giftCardNumber)
                    : lineItem.giftCardNumber
                }`}
              </Text>
            ) : null}
            {lineItem.giftCardNumber ? (
              <Text color="subdued">
                {lineItem.metadata.giftVoucherActivatedDate
                  ? `Activated Date: ${formatDateToCalendar(
                      lineItem.metadata.giftVoucherActivatedDate,
                    )}`
                  : "Not Activated"}
              </Text>
            ) : null}
          </View>
        </View>
        <View style={{ alignItems: "flex-end", paddingLeft: Spacings.s4 }}>
          <Text variant="heading1">
            {moneyToFormattedString(
              bankersRound(lineItem.totalPriceInclusiveTax),
            )}
          </Text>
          {lineItem.adjustments.length >= 1 ? (
            <Text
              color="subdued"
              variant="heading1"
              style={{
                textDecorationLine: "line-through",
              }}
            >
              {moneyToFormattedString(lineItem.totalBasePriceInclTax)}
            </Text>
          ) : undefined}
        </View>
      </View>
    </View>
  );
}

function kitItemToSaleItem(component: any, product: Product) {
  const quantity = component.quantity || 1;

  return {
    product: product,
    name: `${quantity} × ${product.displayName || product.name} @`,
    stockNumber: product.stockNumber,
    quantity: quantity,
    totalPriceInclusiveTax: component.price,
    adjustments: [],
    isKit: false,
    metadata: {},
  };
}
